import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TEMPLATE_BLANK,
  TEMPLATE_SIMPLE,
  TEMPLATE_ORGANIZER,
  stripTemplateContent,
} from '../lib/boardTemplates';
import ModalCloseButton from './ModalCloseButton';

// Order shown in this modal differs from the welcome-setup popup:
// users creating a NEW board (not their first ever) most often want the
// quickest path — Simple comes first, Blank second, Organizer last as a
// "heavier choice" option.
const PICKER_TEMPLATES = [TEMPLATE_SIMPLE, TEMPLATE_BLANK, TEMPLATE_ORGANIZER];

/**
 * NewBoardModal — replaces the old window.prompt('Board name:') with a
 * proper modal: choose a template + give the new board a name. Used by
 * BoardPage's "+ new board" entry in the board dropdown.
 *
 * Defaults to the blank template so the most common path (empty board with
 * a custom name) is one click + type away. Selecting a different template
 * pre-fills the name with the template's defaultBoardName so the user
 * doesn't have to think.
 */
export default function NewBoardModal({ open, onClose, onCreate }) {
  // Default to Simple template — it's the fastest "give me a useful starting
  // canvas" option and the most common new-board creation case.
  const [templateId, setTemplateId] = useState(TEMPLATE_SIMPLE.id);
  // Don't pre-populate the name field — users almost always want to type a
  // descriptive name themselves. Pre-filling forces them to clear it first.
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  // Walter 2026-06-06 (Omar feedback): the "create board" button used to
  // be hard-disabled until a name was typed, so clicking it did nothing
  // and gave no hint why. Now the button stays clickable; an empty-name
  // click (or Enter) nudges the name field instead — highlight + focus +
  // a one-line hint — so the user knows exactly what's missing.
  const [nameNudge, setNameNudge] = useState(false);
  const nudgeTimerRef = useRef(null);
  const triggerNameNudge = useCallback(() => {
    setNameNudge(true);
    requestAnimationFrame(() => inputRef.current?.focus());
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => setNameNudge(false), 1800);
  }, []);

  // Reset on every reopen so stale state never carries over.
  useEffect(() => {
    if (open) {
      setTemplateId(TEMPLATE_SIMPLE.id);
      setName('');
      setSubmitting(false);
      // Focus the name input after a tick so it's mounted.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  // Walter 2026-05-26 — Enter anywhere in the modal triggers create.
  // The form's onKeyDown alone wasn't enough: clicking a template
  // "radio" div moves focus to document.body, and keydowns on body
  // don't bubble DOWN into the form. Document-level keydown listener
  // covers all focus states (input, template div, or nothing focused).
  // Guarded against double-fire while submitting and against empty
  // names. Skip when Shift+Enter for future-proofing a multi-line
  // input. Re-binds when open/name/submitting/templateId change so
  // the listener closes over current state.
  //
  // IMPORTANT: this useEffect MUST be above the `if (!open) return null;`
  // early return below. Putting it after the early return makes the
  // hook conditional on `open`, which violates Rules of Hooks (React
  // error #310: "Rendered more hooks than during the previous render").
  // The `if (!open) return;` GUARD inside the effect is fine — that's
  // not the hook call, that's just deciding whether to bind a listener.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (submitting) return;
      if (!name.trim()) { e.preventDefault(); triggerNameNudge(); return; }
      e.preventDefault();
      // Inline the submit logic so this effect doesn't depend on a
      // closure over handleSubmit (which is defined below the early
      // return and therefore not reliably accessible from up here in
      // every render path).
      const trimmed = name.trim();
      const template =
        PICKER_TEMPLATES.find(t => t.id === templateId) || TEMPLATE_BLANK;
      const seedTemplate = stripTemplateContent(template);
      setSubmitting(true);
      Promise.resolve(onCreate(seedTemplate, trimmed)).catch(err => {
        console.error('New board create failed:', err);
        setSubmitting(false);
      });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, name, submitting, templateId, onCreate, triggerNameNudge]);

  if (!open) return null;

  const pickTemplate = (id) => setTemplateId(id);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const trimmed = (name || '').trim();
    if (!trimmed) { triggerNameNudge(); return; }
    const template =
      PICKER_TEMPLATES.find(t => t.id === templateId) || TEMPLATE_BLANK;
    // Strip pre-populated text so a user creating their second Simple or
    // Organizer board doesn't get the welcome tips / sample content
    // duplicated. They've already seen those on the welcome boards.
    // Layout (positions / titles) is preserved; only `content` is blanked.
    const seedTemplate = stripTemplateContent(template);
    setSubmitting(true);
    try {
      await onCreate(seedTemplate, trimmed);
      // Parent closes the modal.
    } catch (err) {
      console.error('New board create failed:', err);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => !submitting && onClose()}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
        padding: '20px',
      }}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
        // Walter 2026-05-26 — Enter-to-submit is wired via a
        // document-level keydown listener in useEffect above (the
        // form's onKeyDown alone couldn't catch Enter when focus
        // landed on a template-card div, which moves focus to
        // document.body — events on body don't bubble DOWN into the
        // form). The form's onSubmit still fires the native way for
        // input-focused Enter; the document listener covers the rest.
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: 420,
          width: '100%',
          padding: '22px 24px 18px',
          boxShadow: 'var(--shadow-modal)',
          fontFamily: 'var(--mono)',
          position: 'relative',
        }}
      >
        {!submitting && (
          <ModalCloseButton onClick={() => onClose?.()} />
        )}
        <div style={{
          fontSize: 'calc(13px * var(--ui-scale, 1))',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '14px',
          paddingRight: 40,
        }}>
          new board
        </div>

        {/* Name field */}
        <label style={{
          display: 'block',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          marginBottom: '5px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          name
        </label>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={e => { setName(e.target.value); if (nameNudge) setNameNudge(false); }}
          placeholder="board name"
          disabled={submitting}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '9px 12px',
            background: nameNudge ? 'var(--accent-dim)' : 'var(--bg)',
            border: nameNudge ? '1px solid var(--accent)' : '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(12px * var(--ui-scale, 1))',
            outline: 'none',
            marginBottom: nameNudge ? '6px' : '18px',
            transition: 'background 0.15s, border-color 0.15s',
          }}
        />
        {nameNudge && (
          <div style={{
            marginBottom: '12px',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--accent)',
            fontFamily: 'var(--mono)',
          }}>
            give your board a name first
          </div>
        )}

        {/* Template radio list */}
        <label style={{
          display: 'block',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          marginBottom: '5px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          template
        </label>
        <div style={{ marginBottom: '20px' }}>
          {PICKER_TEMPLATES.map(t => {
            const active = templateId === t.id;
            return (
              <div
                key={t.id}
                onClick={() => !submitting && pickTemplate(t.id)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '8px 10px',
                  marginBottom: '4px',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${active ? 'var(--accent-dim)' : 'var(--border)'}`,
                  background: active ? 'var(--accent-wash)' : 'transparent',
                  cursor: submitting ? 'default' : 'pointer',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
              >
                <Radio active={active} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    fontWeight: 600,
                    color: active ? 'var(--text)' : 'var(--text-dim)',
                  }}>
                    {t.label}
                  </div>
                  <div style={{
                    marginTop: '2px',
                    fontSize: 'calc(10px * var(--ui-scale, 1))',
                    color: 'var(--text-dim)',
                    lineHeight: 1.5,
                  }}>
                    {t.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              color: 'var(--text-dim)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              cursor: 'pointer',
            }}
          >
            cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            // Walter 2026-06-06: clickable even with an empty name — the
            // submit handler nudges the name field instead of silently
            // doing nothing. Still dimmed to signal "not ready yet".
            style={{
              padding: '8px 16px',
              background: 'var(--accent)',
              color: 'var(--bg)',
              border: '1px solid var(--accent)',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              fontWeight: 600,
              cursor: submitting ? 'default' : 'pointer',
              opacity: (submitting || !name.trim()) ? 0.55 : 1,
              transition: 'opacity 0.15s',
            }}
          >
            {submitting ? 'creating…' : 'create board'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Radio({ active }) {
  return (
    <span style={{
      width: 14, height: 14,
      flexShrink: 0,
      marginTop: 3,
      borderRadius: '50%',
      border: `1px solid ${active ? 'var(--accent)' : 'var(--border-bright)'}`,
      background: 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'border-color 0.12s',
    }}>
      {active && (
        <span style={{
          width: 7, height: 7,
          borderRadius: '50%',
          background: 'var(--accent)',
        }} />
      )}
    </span>
  );
}
